import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import MachineSelect, { useMachineOptions } from "../select/MachineSelect";
import { useSubprocesses } from "../select/SubprocessCells";
import { parseDecimalInput } from "../utils/decimalInput";
import { minutesToHoursText } from "../utils/timeUnits";
import { useUnitMeasurmentOptions } from "../select/UnitMeasurmentSelect";
import HoursInput from "./HoursInput";
import useSpreadsheetKeyboard from "../hooks/useSpreadsheetKeyboard";
import { useTableViewSort } from "../hooks/useTableViewSort";
import { useTableSearchHighlight, SearchCell } from "../hooks/useTableSearchHighlight";
import TableSortTh from "./TableSortTh";
import { RAZMER_STAGE_ID, useRazmerProcessDefinition } from "./OSStageTree";
import { orderedMachineIds } from "../utils/logbookStage";
import { buildProductivityAggregateRows, ProductivityAggregateRow } from "../utils/productivityAggregate";
import { OS_CARD_CLS, OS_CARD_GAP_CLS, OS_CARD_HEADER_CLS, OS_CARD_TITLE_CLS } from "./OSCard";
import LogBookDateTimeCell, { cellCls } from "./LogBookDateTimeCell";
import LogBookRnDurationCell from "./LogBookRnDurationCell";
import { parseDateTime } from "../utils/logBookDateTime";

// Продуктивност — per-machine timing table (controlled by OS.tsx, persisted per ident).
// Uses the shared spreadsheet keyboard nav (arrow keys across cells + Enter/↓ to add a
// row) and the canonical blue focus accent.
// Машина ги ПРЕДПОЛНУВА монтажа / демонтажа+чистење / оператори од /machines (editable
// per ident; not written back). Вкупно = монтажа + машина + демонтажа/чистење.

export interface ProdRow {
  machineId: string | null;
  montageTime: string;
  machineTime: string;
  cleaningTime: string;
  dismountCleanTime: string;
  operatorCount: string;
  setupParams?: ProdSetupParam[];
  // RN-only: operator-entered params ADDITIONAL to the OS-planned `setupParams` above.
  // `setupParams` stays the read-only plan from the normativ; this is the RN twin of
  // `razmerActuals` vs `razmer` on Размерување. Always empty on OS.
  extraSetupParams?: ProdSetupParam[];
  // Hidden data field — which Subprocess (left stage tree) this row belongs to.
  // Not rendered as a column; stamped automatically by OS.tsx from activeStage.
  subprocessId: string;
  // Log Book по подпроцес: this normativ row holds the WHOLE stage (all machines' montage/
  // dismount folded in) — the stage is never seeded from the machine codebook again.
  // See utils/logbookStage.ts. Absent on old per-machine rows.
  perSubprocess?: boolean;
  // RN actual timestamps — unused on OS.
  montageStart?: string;
  montageEnd?: string;
  machineStart?: string;
  machineEnd?: string;
  dismountStart?: string;
  dismountEnd?: string;
  cleaningStart?: string;
  cleaningEnd?: string;
}

export interface ProdSetupParam {
  description: string;
  par: string;
}

export const emptyProdRow = (): ProdRow => ({
  machineId: null, montageTime: "", machineTime: "", cleaningTime: "", dismountCleanTime: "", operatorCount: "", setupParams: [], extraSetupParams: [], subprocessId: "",
  montageStart: "", montageEnd: "", machineStart: "", machineEnd: "", dismountStart: "", dismountEnd: "", cleaningStart: "", cleaningEnd: "",
});

interface Props {
  rows: ProdRow[];
  onChange: (rows: ProdRow[]) => void;
  readOnly?: boolean;
  title?: string;
  allowedMachineIds?: string[];
  readOnlyMachineSelect?: boolean;
  mode?: "os" | "rn";
  groupBySubprocess?: boolean;
  subprocessNameById?: Map<string, string>;
  editableSummary?: boolean;
  /** RN Razmer-only: a complete, typed first montage start was committed with Enter. */
  onMontageStartCommit?: () => void;
  /** RN Razmer-only: clear the transient start indicator when its source is cleared. */
  onMontageStartClear?: () => void;
  onMontageEndCommit?: () => void;
  onMontageEndClear?: () => void;
  onTimingCommit?: (field: AggregateDateField) => void;
  /** Read-only embeds (e.g. the /work-orders row panel): size columns to the container
   *  instead of fixed pixel widths, so the table never needs its own horizontal scroll. */
  fitWidth?: boolean;
}

// Внес од корисник — заеднички парсер со сите OS табели (`utils/decimalInput.ts`).
// Порано овде се менуваше САМО првата запирка, што „1.234,5" го читаше погрешно.
// Задржано е враќањето 0 наместо null: овие вредности влегуваат во собирање времиња.
const num = (v: unknown): number => parseDecimalInput(v) ?? 0;
const asStr = (v: number | null | undefined): string =>
  v === null || v === undefined ? "" : String(v);

// `fitWidth` (see Props): headers drop their fixed pixel widths so the browser sizes
// columns to fit the container instead of forcing an inner horizontal scroll.
const FitWidthContext = React.createContext(false);
const TH: React.FC<{ w: number; children: React.ReactNode }> = ({ w, children }) => {
  const fit = React.useContext(FitWidthContext);
  return (
    <th
      className={`border border-gray-300 bg-gray-200 text-gray-700 font-semibold text-center h-[42px] ${fit ? "px-0.5 text-[10px] [overflow-wrap:anywhere]" : "px-2.5 text-[11px]"} py-1.5 leading-tight align-middle`}
      style={fit ? undefined : { width: w, minWidth: w }}
    >
      {children}
    </th>
  );
};

// Short header („Монтажа“) — the full name „Времетраење на монтажа“ is the tooltip.
// `short`: RN's narrower columns show an abbreviation („Мон. РН“); the tooltip stays the full name.
export const DurationHeader: React.FC<{ label: string; short?: string }> = ({ label, short }) => (
  <span title={`Времетраење на ${label} (ч:мм)`}>{short || label.charAt(0).toUpperCase() + label.slice(1)}</span>
);

// Log Book times are STORED in minutes and shown/entered as hours:minutes „0:30“ (decision
// 2026-10-07, utils/timeUnits.ts). A Log Book row is a whole stage (no machine of its own),
// so the machine's ЕМ never applies here — every time is h:mm.
const LOGBOOK_TIME_UNIT = "h";

const SubprocessBandRow: React.FC<{
  label: string;
  machineCount: number;
  colSpan: number;
}> = ({ label, machineCount, colSpan }) => (
  <tr>
    <td
      colSpan={colSpan}
      className="border border-blue-200 bg-blue-50 h-[26px] px-3 py-0.5 text-[11px] font-semibold text-blue-900"
    >
      {label} · {machineCount === 1 ? "1 машина" : `${machineCount} машини`}
    </td>
  </tr>
);

// Actual elapsed time between a Почеток/Крај pair, in MINUTES.
//
// Minutes, not decimal hours: the planned columns next to these are minutes
// (machine.setupTimeUnit resolves to the `min` unit, which is also what the ЕМ
// column at the end of the row states), so an hours value would sit under a
// header that says `min` and could not be compared against its own plan.
// 10:00 → 10:20 is 20, not 0,33.
//
// Returns null — not 0 — when the pair is unusable, so the caller can tell
// "not filled in yet" apart from "end is before start" (real data exists:
// РН 26-6020-00027 cleaning runs 12:35 → 12:30).
const durationMinutes = (start: string | undefined, end: string | undefined): number | null => {
  const startDate = parseDateTime(start);
  const endDate = parseDateTime(end);
  if (!startDate || !endDate) return null;
  return Math.round((endDate.getTime() - startDate.getTime()) / 60000);
};

// Empty while a pair is incomplete; the raw negative value when the range is
// inverted, so a bad entry is visible on the floor instead of silently reading
// as a blank cell.
// The eight Почеток/Крај fields elapsedMinutes() scans — every timing stage's start and end.
const ELAPSED_FIELDS: Array<keyof ProdRow> = [
  "montageStart", "montageEnd",
  "machineStart", "machineEnd",
  "dismountStart", "dismountEnd",
  "cleaningStart", "cleaningEnd",
];
// Detail-row twin of buildProductivityAggregateRows' elapsedRnTime: minutes between the
// earliest and latest parseable timestamp across this single row's own eight fields. A
// min/max span, so — unlike durationMinutes on a single pair — it can never be negative and
// needs no invalid-duration flagging. Fewer than two parseable timestamps → null (not 0),
// same convention as durationMinutes.
const elapsedMinutes = (row: ProdRow): number | null => {
  let min: number | null = null;
  let max: number | null = null;
  let count = 0;
  ELAPSED_FIELDS.forEach((field) => {
    const date = parseDateTime(row[field] as string | undefined);
    if (!date) return;
    const time = date.getTime();
    count += 1;
    if (min === null || time < min) min = time;
    if (max === null || time > max) max = time;
  });
  if (count < 2 || min === null || max === null) return null;
  return Math.round((max - min) / 60000);
};
const sumDurations = (values: Array<number | null>): string => {
  let total = 0;
  let count = 0;
  values.forEach((value) => {
    if (value === null) return;
    total += value;
    count += 1;
  });
  return count > 0 ? String(total) : "";
};
const compactDateTime = (value: string | undefined): string => String(value ?? "").trim().split(/\s+/).pop() || "";
// Shared by the „Вкупно време (РН)" cell and the „РН: N h" badge — both mark the same
// condition (a negative Почеток/Крај pair excluded from the total).
const invalidDurationTitle = "Исклучено е времетраење со крај пред почеток — поправи го во Log Book на фазата";
const isAggregateRow = (row: ProdRow | ProductivityAggregateRow): row is ProductivityAggregateRow =>
  "aggregate" in row && row.aggregate === true;
type AggregateEditableField = "montageTime" | "machineTime" | "dismountCleanTime" | "cleaningTime";
// RN actuals that the operator edits straight on the grouped (per-subprocess) row.
type AggregateDateField =
  | "montageStart" | "montageEnd"
  | "machineStart" | "machineEnd"
  | "dismountStart" | "dismountEnd"
  | "cleaningStart" | "cleaningEnd";

const OSProductivityTable: React.FC<Props> = ({
  rows,
  onChange,
  readOnly = false,
  title = "Log Book",
  allowedMachineIds,
  readOnlyMachineSelect = false,
  fitWidth = false,
  mode = "os",
  groupBySubprocess = false,
  subprocessNameById,
  editableSummary = false,
  onMontageStartCommit,
  onMontageStartClear,
  onMontageEndCommit,
  onMontageEndClear,
  onTimingCommit,
}) => {
  const isRn = mode === "rn";
  const { t } = useTranslation();
  const machineOptions = useMachineOptions();
  const inputRefs = useRef<(HTMLInputElement | HTMLSelectElement | null)[][]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  // Grouped Log Books always show one subtotal row per subprocess — the per-machine
  // detailed view was removed; there is no toggle back to it.
  const isSummaryMode = groupBySubprocess;
  const showChrome = !readOnly && !isSummaryMode;

  const patchAggregateField = (aggregate: ProductivityAggregateRow, field: AggregateEditableField, value: string) => {
    if (readOnly || !editableSummary || aggregate.sourceIndices.length === 0) return;
    const oldTotal = aggregate.sourceIndices.reduce((sum, index) => sum + num(rows[index]?.[field]), 0);
    const nextTotal = num(value);
    const firstIndex = aggregate.sourceIndices[0];
    const firstOld = num(rows[firstIndex]?.[field]);
    const firstNext = firstOld + (nextTotal - oldTotal);
    onChange(rows.map((row, index) => index === firstIndex ? { ...row, [field]: String(firstNext) } : row));
  };

  // Почеток/Крај on a grouped row, in priority order:
  //
  //   1. The machine row that SUPPLIED the displayed value. The aggregate shows the earliest
  //      start / latest end of the group, so writing to sourceIndices[0] would leave the
  //      original in place and the aggregate would go on showing it.
  //   2. When the cell is still empty — the machine row that already holds the OTHER HALF of
  //      the pair. Размерување runs on two scales (РН 26-6020-00027): drop the Крај on the
  //      first row while the Почеток sits on the second and neither row holds a usable pair,
  //      so „Времетраење (РН)" reads empty even though both cells show a time.
  //   3. Otherwise the first row of the group.
  const pairFieldOf = (field: AggregateDateField): AggregateDateField =>
    (field.endsWith("Start") ? field.replace(/Start$/, "End") : field.replace(/End$/, "Start")) as AggregateDateField;

  const patchAggregateDate = (aggregate: ProductivityAggregateRow, field: AggregateDateField, value: string) => {
    if (readOnly || !editableSummary || aggregate.sourceIndices.length === 0) return;
    const shown = aggregate[field] || "";
    const pairField = pairFieldOf(field);
    const holdsShown = shown
      ? aggregate.sourceIndices.find((index) => (rows[index]?.[field] || "") === shown)
      : undefined;
    const holdsPair = aggregate.sourceIndices.find((index) => Boolean(rows[index]?.[pairField]));
    const targetIndex = holdsShown ?? holdsPair ?? aggregate.sourceIndices[0];
    onChange(rows.map((row, index) => (index === targetIndex ? { ...row, [field]: value } : row)));
  };

  // Plan columns stay read-only on RN (planLocked) — the RN Log Book edits actuals only.
  const aggregateInput = (row: ProductivityAggregateRow, field: AggregateEditableField) =>
    editableSummary && !planLocked
      ? <HoursInput className={planInputCls} minutes={row[field]} onMinutesChange={(v) => patchAggregateField(row, field, v)} />
      : minutesToHoursText(row[field]);

  const aggregateDateCell = (row: ProductivityAggregateRow, field: AggregateDateField) =>
    editableSummary && !readOnly
      ? (
        <LogBookDateTimeCell
          value={row[field] ?? ""}
          onChange={(value) => {
            patchAggregateDate(row, field, value);
            if (field === "montageStart" && !value) onMontageStartClear?.();
            if (field === "montageEnd" && !value) onMontageEndClear?.();
          }}
          disabled={isSorted}
          compact
          baseDate={row.montageStart ?? ""}
          onCommit={field === "montageStart" ? onMontageStartCommit : field === "montageEnd" ? onMontageEndCommit : undefined}
        />
      )
      : compactDateTime(row[field]);

  const subprocessOptions = useSubprocesses();
  const machineById = useMemo(() => {
    const map = new Map<string, (typeof machineOptions)[number]>();
    machineOptions.forEach((m) => map.set(m._id, m));
    return map;
  }, [machineOptions]);

  // Machine ЕМ code — only feeds the aggregate rows' `unit`; Log Book times are always h:mm.
  const unitOptions = useUnitMeasurmentOptions();
  const unitOf = useCallback((id: string | null): string => {
    const machine = id ? machineById.get(id) : undefined;
    if (!machine) return "";
    const ref = machine.setupTimeUnit;
    const unit = ref && typeof ref === "object" ? ref : unitOptions.find((u) => u._id === ref);
    return unit?.code || "min";
  }, [machineById, unitOptions]);

  const machineDisplayOf = useCallback((id: string | null): string => {
    if (!id) return "";
    const machine = machineById.get(id);
    if (!machine) return "";
    return machine.name || "";
  }, [machineById]);

  // Log Book по подпроцес: the machines of a stage are information only — listed from the
  // codebook (cached), never a row of their own and never carrying times.
  const razmerProcessDefinition = useRazmerProcessDefinition();
  const stageMachineIdsOf = useCallback((subprocessId: string): string[] => {
    if (subprocessId === RAZMER_STAGE_ID) return orderedMachineIds(razmerProcessDefinition?.machines);
    return orderedMachineIds(subprocessOptions.find((sp) => sp._id === subprocessId)?.machines);
  }, [subprocessOptions, razmerProcessDefinition]);
  const stageMachineNamesOf = useCallback((subprocessId: string): string => (
    stageMachineIdsOf(subprocessId).map(machineDisplayOf).filter(Boolean).join(", ")
  ), [stageMachineIdsOf, machineDisplayOf]);

  const patch = (i: number, p: Partial<ProdRow>) => {
    if (readOnly || isRn) return;
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...p } : r)));
  };

  const patchActual = (i: number, p: Partial<ProdRow>) => {
    if (readOnly || !isRn) return;
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...p } : r)));
  };

  const setRows = (u: React.SetStateAction<ProdRow[]>) => {
    if (readOnly || isRn) return;
    onChange(typeof u === "function" ? (u as (p: ProdRow[]) => ProdRow[])(rows) : u);
  };
  const hasRowData = (r: ProdRow) =>
    !!(r.machineId || r.montageTime || r.machineTime || r.cleaningTime || r.dismountCleanTime || r.operatorCount);
  const { handleKeyDown } = useSpreadsheetKeyboard({ rows, setRows, inputRefs, createEmptyRow: emptyProdRow, hasRowData });

  const reg = (i: number, col: number) => ({
    ref: (el: HTMLInputElement | HTMLSelectElement | null) => {
      if (!inputRefs.current[i]) inputRefs.current[i] = [];
      inputRefs.current[i][col] = el;
    },
    onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
      if (!readOnly) handleKeyDown(e, i, col);
    },
  });

  // Празен `subprocessId` е ред што уште не е пополнет — таму нема што да се именува.
  // „Непознат подпроцес" се пишува САМО кога id-то е поставено но не се разрешува:
  // тоа е заостаната фаза од увоз и е сигнал што не смее да се крие.
  const subprocessLabelOf = useCallback((subprocessId: string): string => {
    if (!subprocessId) return "";
    if (subprocessId === RAZMER_STAGE_ID) return "Размерување";
    return subprocessNameById?.get(subprocessId) || "Непознат подпроцес";
  }, [subprocessNameById]);

  const aggregateRows = useMemo(
    () => buildProductivityAggregateRows({ rows, subprocessLabelOf, unitOf, durationMinutes, parseDateTime }),
    [rows, subprocessLabelOf, unitOf]
  );
  const displayRows: Array<ProdRow | ProductivityAggregateRow> = isSummaryMode ? aggregateRows : rows;

  // ─── View sort (presentation only — never persisted, never reorders `rows`) ────────────
  const getProdValue = useCallback((row: ProdRow | ProductivityAggregateRow, field: string): string | number => {
    if (isAggregateRow(row)) {
      switch (field) {
        case "machine":
          return row.machineLabel;
        case "montageTime":
          return num(row.montageTime);
        case "machineTime":
          return num(row.machineTime);
        case "dismountCleanTime":
          return num(row.dismountCleanTime);
        case "cleaningTime":
          return num(row.cleaningTime);
        case "operatorCount":
          // Aggregate cell can hold a distribution string like "2, 3" — sort by the
          // numeric max instead of parsing the display string.
          return row.operatorCountMax;
        case "total":
          return num(row.total);
        case "totalRnTime":
          return num(row.totalRnTime);
        case "elapsedRnTime":
          return num(row.elapsedRnTime);
        default:
          return "";
      }
    }
    switch (field) {
      case "machine":
        return isRn ? subprocessLabelOf(row.subprocessId) : machineDisplayOf(row.machineId);
      case "montageTime":
        return num(row.montageTime);
      case "machineTime":
        return num(row.machineTime);
      case "dismountCleanTime":
        return num(row.dismountCleanTime);
      case "cleaningTime":
        return num(row.cleaningTime);
      case "operatorCount":
        return num(row.operatorCount);
      case "total":
        // Always the planned total, in minutes, in both modes. The RN columns
        // are actuals and belong to their own pairs; summing them here would
        // make one column mean "planned minutes" on /os and "actual minutes"
        // on the work order.
        return num(row.montageTime) + num(row.machineTime) + num(row.cleaningTime) + num(row.dismountCleanTime);
      case "totalRnTime":
        return (
          (durationMinutes(row.montageStart, row.montageEnd) ?? 0) +
          (durationMinutes(row.machineStart, row.machineEnd) ?? 0) +
          (durationMinutes(row.dismountStart, row.dismountEnd) ?? 0) +
          (durationMinutes(row.cleaningStart, row.cleaningEnd) ?? 0)
        );
      case "elapsedRnTime":
        return elapsedMinutes(row) ?? 0;
      default:
        return "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machineDisplayOf, isRn, subprocessLabelOf]);

  // Log Book (и „Вкупно Log Book“): редоследот = редоследот на производство и има 1–5 реда,
  // па сортирање само збунува и ги брише групите — без сорт (одлука 2026-10-08).
  const sortDisabled = true;
  const { sortField, sortDirection, handleSort, resetSort, isSorted, sortedItems } =
    useTableViewSort<ProdRow | ProductivityAggregateRow>(displayRows, getProdValue);

  const tableColumnCount = useMemo(() => {
    let count = 8; // machine, four planned durations, operators, total, unit
    if (showChrome) count += 2;
    else if (isRn) count += 1;
    if (isRn) count += 12; // start/end/RN duration for four timing groups
    if (isRn) count += 1; // total RN duration
    if (isRn) count += 1; // elapsed RN duration (Изминато време)
    return count;
  }, [isRn, showChrome]);

  const showSubprocessBands = groupBySubprocess && !isSummaryMode && !isSorted;

  const subprocessMachineCounts = useMemo(() => {
    const counts = new Map<string, number>();
    if (!showSubprocessBands) return counts;
    sortedItems.forEach(({ item }) => {
      const key = item.subprocessId || "";
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  }, [showSubprocessBands, sortedItems]);

  // ─── Search (marks + jumps — never filters, never reorders) ────────────────────────────
  const searchCells = useMemo<SearchCell[]>(() => {
    const cells: SearchCell[] = [];
    sortedItems.forEach(({ item: row, index }) => {
      const name = isAggregateRow(row) ? row.machineLabel : machineDisplayOf(row.machineId);
      if (name) cells.push({ id: `${index}:machine`, text: name });
    });
    return cells;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedItems, machineDisplayOf]);

  const {
    searchInput, setSearchInput, searchTerm, handleSearch, clearSearch,
    matchCount, activeIndex, next, prev, registerCell, isMatch, isActive,
  } = useTableSearchHighlight(searchCells);

  const cellHighlightCls = (id: string): string => {
    if (isActive(id)) return " bg-blue-300 dark:bg-blue-700/70 ring-2 ring-blue-500";
    if (isMatch(id)) return " bg-blue-100 dark:bg-blue-900/40";
    return "";
  };

  const totalBadges = useMemo(() => {
    const plannedTotal = rows.reduce(
      (sum, row) => sum + num(row.montageTime) + num(row.machineTime) + num(row.cleaningTime) + num(row.dismountCleanTime),
      0
    );
    let rnTotal = 0;
    let rnCount = 0;
    // A negative pair (end before start) is invalid data, not a duration — excluded from
    // the badge total the same way as the aggregate rows, and flagged so it can be marked.
    let rnHasInvalid = false;
    rows.forEach((row) => {
      [
        durationMinutes(row.montageStart, row.montageEnd),
        durationMinutes(row.machineStart, row.machineEnd),
        durationMinutes(row.dismountStart, row.dismountEnd),
        durationMinutes(row.cleaningStart, row.cleaningEnd),
      ].forEach((value) => {
        if (value === null) return;
        if (value < 0) {
          rnHasInvalid = true;
          return;
        }
        rnTotal += value;
        rnCount += 1;
      });
    });
    // Split base/variant instead of appending an override: two utilities of the same
    // kind (text-white + text-yellow-800, bg-white/15 + bg-yellow-400/25) resolve by
    // their order in the generated CSS, NOT by their order in the class string, so an
    // appended override is a coin flip. The badges also sit on the dark gradient
    // toolbar, where dark-on-dark text would be unreadable — the warning variant keeps
    // white text and signals through its border/background instead.
    const badgeBase = "rounded-md border px-2 py-1 text-[11px] font-semibold text-white whitespace-nowrap";
    const badgeCls = `${badgeBase} border-white/30 bg-white/15`;
    const badgeWarnCls = `${badgeBase} border-yellow-300/70 bg-yellow-400/25`;
    return (
      <div className="flex items-center gap-1.5">
        <span className={badgeCls}>ОС: {minutesToHoursText(plannedTotal) || "0:00"}</span>
        {isRn && (
          <span
            className={rnHasInvalid ? badgeWarnCls : badgeCls}
            title={rnHasInvalid ? invalidDurationTitle : undefined}
          >
            РН: {rnCount > 0 ? minutesToHoursText(rnTotal) || "0:00" : "0:00"}{rnHasInvalid ? " ⚠" : ""}
          </span>
        )}
      </div>
    );
  }, [isRn, rows]);

  // On machine select, prefill the editable fields from the codebook (defaults for this ident).
  const selectMachine = (i: number, id: string | null) => {
    if (readOnly || readOnlyMachineSelect) return;
    if (!id) {
      patch(i, { machineId: null, montageTime: "", cleaningTime: "", dismountCleanTime: "", operatorCount: "" });
      return;
    }
    const m = machineById.get(id);
    patch(i, {
      machineId: id,
      montageTime: asStr(m?.setupTime ?? null),
      dismountCleanTime: asStr(m?.cleaningDismountTime ?? null),
      operatorCount: asStr(subprocessOptions.find((sp) => sp._id === rows[i]?.subprocessId)?.operatorCount ?? null),
    });
  };

  const isAllSelected = rows.length > 0 && selected.size === rows.length;
  const isSomeSelected = selected.size > 0;

  const toggleAll = () => {
    if (readOnly || isRn || isSorted) return;
    setSelected(isAllSelected ? new Set() : new Set(rows.map((_, i) => i)));
  };

  const toggleRow = (i: number) => {
    if (readOnly) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const deleteSelected = () => {
    if (readOnly || isRn || isSorted) return;
    const kept = rows.filter((_, i) => !selected.has(i));
    onChange(kept.length === 0 ? [emptyProdRow()] : kept);
    setSelected(new Set());
    inputRefs.current = [];
  };

  // Only a genuinely read-only table (readOnly=true — the "Вкупна продуктивност" summary)
  // hides row-selection chrome.
  const planLocked = readOnly || isRn;
  const sortLocked = isSorted;
  // Darkened "plan" shade for RN — mirrors OSRazmerTable's planReadOnlyCellBase/planCellCls.
  // Tied ONLY to isRn (never to planLocked/readOnly alone), so /os stays pixel-identical and
  // the readOnly "Вкупна продуктивност" summary (readOnly=true, isRn=true) keeps the same
  // darker shade as the live per-stage RN table, not the plain /os readOnly look.
  const planCellCls = isRn ? " bg-slate-50 cursor-default" : ""; // light tint = pulled from the normativ (2026-10-09), not a dark disabled block
  const planInputCls = isRn
    ? "w-full h-6 px-1 border-0 outline-none text-[11px] bg-transparent text-right tabular-nums text-gray-600 cursor-default"
    : cellCls;
  const planMachineTextCls = isRn
    ? "w-full h-6 px-1 border-0 outline-none text-[11px] bg-transparent text-left text-gray-600 cursor-default"
    : "w-full h-6 px-1 border-0 outline-none text-[11px] bg-transparent text-left text-gray-700";
  // The text colour is kept OUT of the base so the warning variant can swap it instead of
  // appending a second text-* utility: two colour utilities on one element resolve by their
  // order in the generated CSS, not in the class string, so an appended override is a coin flip.
  const textCellBase = "border border-gray-300 h-[26px] px-1.5 py-0.5 text-[11px] bg-gray-50";
  // Footer „Вкупно“ of the per-subprocess view: column sums of the aggregate rows (minutes).
  const summaryTotals = useMemo(() => {
    if (!isSummaryMode) return null;
    const agg = sortedItems.map(({ item }) => item).filter(isAggregateRow);
    if (agg.length === 0) return null;
    const sum = (field: "montageTime" | "machineTime" | "dismountCleanTime" | "cleaningTime" | "total") => {
      const total = agg.reduce((acc, row) => acc + (Number(String(row[field] ?? "").replace(",", ".")) || 0), 0);
      return total > 0 ? minutesToHoursText(total) : "";
    };
    return {
      montageTime: sum("montageTime"),
      machineTime: sum("machineTime"),
      dismountCleanTime: sum("dismountCleanTime"),
      cleaningTime: sum("cleaningTime"),
      total: sum("total"),
    };
  }, [isSummaryMode, sortedItems]);
  const textCellCls = `${textCellBase} text-gray-700`;
  const rightTextCellCls = `${textCellCls} text-right tabular-nums`;
  const centerTextCellCls = `${textCellCls} text-center`;
  // „Вкупно време (РН)" on a group that had an invalid pair excluded from its sum.
  const rightTextCellWarnCls = `${textCellBase} text-yellow-800 font-semibold text-right tabular-nums`;
  // An editable Почеток/Крај drops the read-only grey fill so the grouped row reads as an input.
  const aggregateDateTdCls = editableSummary && !readOnly
    ? "border border-gray-300 h-[26px] px-1.5 py-0.5"
    : rightTextCellCls;

  return (
    <div className={`${OS_CARD_CLS} ${OS_CARD_GAP_CLS} max-w-full`}>
      <div className={OS_CARD_HEADER_CLS}>
        <h2 className={OS_CARD_TITLE_CLS}>{title}</h2>
        {/* No search here: Log Book has one row per subprocess (a handful per normativ), so
            everything is visible at once — only the ОС/РН totals are shown. */}
        <div className="flex items-center gap-1.5">{totalBadges}</div>
      </div>
      {isSorted && (
        <div className="bg-blue-50 dark:bg-blue-900/30 border-b border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200 text-[11px] px-3 py-1.5">
          {t("tableTools.sortLockNotice")}
        </div>
      )}
      <FitWidthContext.Provider value={fitWidth}>
      <div className={fitWidth ? "bg-white" : "overflow-x-auto bg-white"}>
        <table className={`border-collapse w-full ${fitWidth ? "" : isRn ? "min-w-[1100px]" : readOnly ? "min-w-[1050px]" : "min-w-[1090px]"}`}>
          <thead>
            <tr>
              {showChrome && (
                <>
                  <th className="border border-gray-300 bg-gray-200 w-8 h-[26px] px-1">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                      checked={isAllSelected}
                      ref={(el) => { if (el) el.indeterminate = isSomeSelected && !isAllSelected; }}
                      onChange={toggleAll}
                      disabled={isSorted || isRn}
                    />
                  </th>
                  <th className="border border-gray-300 bg-gray-200 w-6 h-[26px] px-1">
                    <button
                      onClick={deleteSelected}
                      disabled={!isSomeSelected || isSorted || isRn}
                      className={`${isSomeSelected && !isSorted && !isRn ? "text-red-500 hover:text-red-700 cursor-pointer" : "text-gray-300 cursor-not-allowed"} transition-colors`}
                      title={isRn ? "" : (isSomeSelected ? `Избриши ${selected.size} избрани редови` : "Нема избрани редови")}
                    >
                      <Trash2 size={14} className="mx-auto" />
                    </button>
                  </th>
                </>
              )}
              {isRn && !showChrome && (
                <th className="border border-gray-300 bg-gray-200 text-gray-700 font-semibold text-center text-[11px] w-8 h-[26px] px-1">
                  #
                </th>
              )}
              <TableSortTh w={isRn ? 170 : 200} field="machine" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} disabled={sortDisabled}>
                {isSummaryMode || isRn ? "Подпроцес" : "Машини"}
              </TableSortTh>
              <TableSortTh w={isRn ? 80 : 130} field="montageTime" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} disabled={sortDisabled}>
                <DurationHeader label="монтажа" />
              </TableSortTh>
              {isRn && (
                <>
                  <TH w={60}><span title="Почеток">Поч.</span></TH>
                  <TH w={60}>Крај</TH>
                  <TH w={80}>
                    <DurationHeader label="монтажа (РН)" short="Мон. РН" />
                  </TH>
                </>
              )}
              <TableSortTh w={isRn ? 80 : 130} field="machineTime" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} disabled={sortDisabled}>
                <DurationHeader label="машина" />
              </TableSortTh>
              {isRn && (
                <>
                  <TH w={60}><span title="Почеток">Поч.</span></TH>
                  <TH w={60}>Крај</TH>
                  <TH w={80}>
                    <DurationHeader label="машина (РН)" short="Маш. РН" />
                  </TH>
                </>
              )}
              <TableSortTh w={isRn ? 80 : 150} field="dismountCleanTime" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} disabled={sortDisabled}>
                <DurationHeader label="демонтажа" />
              </TableSortTh>
              {isRn && (
                <>
                  <TH w={60}><span title="Почеток">Поч.</span></TH>
                  <TH w={60}>Крај</TH>
                  <TH w={80}>
                    <DurationHeader label="демонтажа (РН)" short="Дем. РН" />
                  </TH>
                </>
              )}
              <TableSortTh w={isRn ? 80 : 140} field="cleaningTime" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} disabled={sortDisabled}>
                <DurationHeader label="чистење" />
              </TableSortTh>
              {isRn && (
                <>
                  <TH w={60}><span title="Почеток">Поч.</span></TH>
                  <TH w={60}>Крај</TH>
                  <TH w={80}>
                    <DurationHeader label="чистење (РН)" short="Чис. РН" />
                  </TH>
                </>
              )}
              <TableSortTh w={isRn ? 70 : 100} field="operatorCount" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} disabled={sortDisabled}>
                <span title="Број на оператори">{isRn ? "Опер." : "Оператори"}</span>
              </TableSortTh>
              <TableSortTh w={isRn ? 75 : 100} field="total" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} disabled={sortDisabled}>
                <span title="Вкупно време">Вкупно</span>
              </TableSortTh>
              {isRn && (
                <TableSortTh w={80} field="totalRnTime" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} disabled={sortDisabled}>
                  <span title="Вкупно време (РН) — собрано машинско време, сумата на времетраењата по машина">Вк. РН</span>
                </TableSortTh>
              )}
              {isRn && (
                <TableSortTh w={80} field="elapsedRnTime" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} disabled={sortDisabled}>
                  <span title="Изминато време — од првиот почеток до последниот крај на подпроцесот">Изм. време</span>
                </TableSortTh>
              )}
              <TH w={60}>ЕМ</TH>
            </tr>
          </thead>
          <tbody>
            {sortedItems.map(({ item: row, index: i }, displayIndex) => {
              if (isAggregateRow(row)) {
                const machineCellId = `${i}:machine`;
                return (
                  <tr key={`summary:${row.subprocessId || displayIndex}`}>
                    {isRn && !showChrome && (
                      <td className={centerTextCellCls}>
                        {displayIndex + 1}
                      </td>
                    )}
                    <td
                      ref={registerCell(machineCellId)}
                      className={`${textCellCls}${cellHighlightCls(machineCellId)}`}
                      title={[row.machineLabel, stageMachineNamesOf(row.subprocessId)].filter(Boolean).join(" · ")}
                    >
                      {row.machineLabel}
                      {stageMachineNamesOf(row.subprocessId) && (
                        <span className="block whitespace-normal break-words text-[10px] leading-tight text-gray-500">
                          {t("os.logbookMachines", "Машини")}: {stageMachineNamesOf(row.subprocessId)}
                        </span>
                      )}
                    </td>
                    <td className={rightTextCellCls}>{aggregateInput(row, "montageTime")}</td>
                    {isRn && (
                      <>
                        <td className={aggregateDateTdCls} title={row.montageStart}>{aggregateDateCell(row, "montageStart")}</td>
                        <td className={aggregateDateTdCls} title={row.montageEnd}>{aggregateDateCell(row, "montageEnd")}</td>
                        <LogBookRnDurationCell actual={row.montageRnTime} planned={row.montageTime} className={rightTextCellCls} />
                      </>
                    )}
                    <td className={rightTextCellCls}>{aggregateInput(row, "machineTime")}</td>
                    {isRn && (
                      <>
                        <td className={aggregateDateTdCls} title={row.machineStart}>{aggregateDateCell(row, "machineStart")}</td>
                        <td className={aggregateDateTdCls} title={row.machineEnd}>{aggregateDateCell(row, "machineEnd")}</td>
                        <LogBookRnDurationCell actual={row.machineRnTime} planned={row.machineTime} className={rightTextCellCls} />
                      </>
                    )}
                    <td className={rightTextCellCls}>{aggregateInput(row, "dismountCleanTime")}</td>
                    {isRn && (
                      <>
                        <td className={aggregateDateTdCls} title={row.dismountStart}>{aggregateDateCell(row, "dismountStart")}</td>
                        <td className={aggregateDateTdCls} title={row.dismountEnd}>{aggregateDateCell(row, "dismountEnd")}</td>
                        <LogBookRnDurationCell actual={row.dismountRnTime} planned={row.dismountCleanTime} className={rightTextCellCls} />
                      </>
                    )}
                    <td className={rightTextCellCls}>{aggregateInput(row, "cleaningTime")}</td>
                    {isRn && (
                      <>
                        <td className={aggregateDateTdCls} title={row.cleaningStart}>{aggregateDateCell(row, "cleaningStart")}</td>
                        <td className={aggregateDateTdCls} title={row.cleaningEnd}>{aggregateDateCell(row, "cleaningEnd")}</td>
                        <LogBookRnDurationCell actual={row.cleaningRnTime} planned={row.cleaningTime} className={rightTextCellCls} />
                      </>
                    )}
                    <td
                      className={rightTextCellCls}
                      title="Најголемиот број оператори во подпроцесот — не е збир"
                    >
                      {row.operatorCountMax > 0 ? row.operatorCountMax : ""}
                    </td>
                    <td className={rightTextCellCls}>{minutesToHoursText(row.total)}</td>
                    {isRn && (
                      <td
                        className={row.hasInvalidDuration ? rightTextCellWarnCls : rightTextCellCls}
                        title={row.hasInvalidDuration ? invalidDurationTitle : undefined}
                      >
                        {minutesToHoursText(row.totalRnTime)}{row.hasInvalidDuration ? " ⚠" : ""}
                      </td>
                    )}
                    {isRn && (
                      <td className={rightTextCellCls}>{minutesToHoursText(row.elapsedRnTime)}</td>
                    )}
                    <td className={centerTextCellCls}>{LOGBOOK_TIME_UNIT}</td>
                  </tr>
                );
              }
              const montageRnTime = durationMinutes(row.montageStart, row.montageEnd);
              const machineRnTime = durationMinutes(row.machineStart, row.machineEnd);
              const dismountRnTime = durationMinutes(row.dismountStart, row.dismountEnd);
              const cleaningRnTime = durationMinutes(row.cleaningStart, row.cleaningEnd);
              const totalRnTime = sumDurations([montageRnTime, machineRnTime, dismountRnTime, cleaningRnTime]);
              const total = num(row.montageTime) + num(row.machineTime) + num(row.cleaningTime) + num(row.dismountCleanTime);
              const subprocessId = row.subprocessId || "";
              const previousSubprocessId = sortedItems[displayIndex - 1]?.item.subprocessId || "";
              const showBand = showSubprocessBands && (displayIndex === 0 || subprocessId !== previousSubprocessId);
              return (
                <React.Fragment key={i}>
                  {showBand && (
                    <SubprocessBandRow
                      label={subprocessLabelOf(subprocessId)}
                      machineCount={subprocessMachineCounts.get(subprocessId) || 0}
                      colSpan={tableColumnCount}
                    />
                  )}
                <tr className="hover:bg-blue-50 focus-within:bg-blue-50">
                  {showChrome && (
                    <>
                      <td className="border border-gray-300 h-[26px] px-0 py-0 text-center">
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                          checked={selected.has(i)}
                          onChange={() => toggleRow(i)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={isSorted}
                        />
                      </td>
                      <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-center text-[11px] text-gray-700">
                        {i + 1}
                      </td>
                    </>
                  )}
                  {isRn && !showChrome && (
                    <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-center text-[11px] text-gray-700">
                      {i + 1}
                    </td>
                  )}
                  <td
                    ref={registerCell(`${i}:machine`)}
                    className={`border border-gray-300 h-[26px] px-1 py-0.5${planCellCls}${cellHighlightCls(`${i}:machine`)}`}
                  >
                    {readOnlyMachineSelect || isRn ? (
                      <input
                        type="text"
                        className={planMachineTextCls}
                        value={isRn ? subprocessLabelOf(row.subprocessId) : machineDisplayOf(row.machineId)}
                        readOnly
                        title={isRn ? subprocessLabelOf(row.subprocessId) : machineDisplayOf(row.machineId)}
                        {...(!isSorted ? reg(i, 1) : {})}
                      />
                    ) : (
                      <MachineSelect
                        value={row.machineId}
                        onChange={(id) => selectMachine(i, id)}
                        disabled={readOnly || sortLocked}
                        allowedMachineIds={allowedMachineIds}
                        size="table"
                        className="w-full"
                        displayNameOnly
                        hideArrow
                        {...(!isSorted ? reg(i, 1) : {})}
                      />
                    )}
                  </td>
                  <td className={`border border-gray-300 h-[26px] px-1.5 py-0.5${planCellCls}`}>
                    <HoursInput className={planInputCls} minutes={row.montageTime} readOnly={planLocked || sortLocked} disabled={sortLocked} onMinutesChange={(v) => patch(i, { montageTime: v })} {...(!isSorted ? reg(i, 3) : {})} />
                  </td>
                  {isRn && (
                    <>
                      <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
                        <LogBookDateTimeCell
                          value={row.montageStart ?? ""}
                          onChange={(value) => {
                            patchActual(i, { montageStart: value });
                            if (!value) onMontageStartClear?.();
                          }}
                          disabled={readOnly || sortLocked}
                          compact
                          baseDate={row.montageStart ?? ""}
                          onCommit={onMontageStartCommit}
                        />
                      </td>
                      <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
                        <LogBookDateTimeCell value={row.montageEnd ?? ""} onChange={(value) => { patchActual(i, { montageEnd: value }); if (!value) onMontageEndClear?.(); }} onCommit={onMontageEndCommit} disabled={readOnly || sortLocked} compact baseDate={row.montageStart ?? ""} />
                      </td>
                      <LogBookRnDurationCell
                        actual={montageRnTime}
                        planned={row.montageTime}
                        className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-right tabular-nums text-[11px] text-gray-700 bg-gray-50"
                      />
                    </>
                  )}
                  <td className={`border border-gray-300 h-[26px] px-1.5 py-0.5${planCellCls}`}>
                    <HoursInput className={planInputCls} minutes={row.machineTime} readOnly={planLocked || sortLocked} disabled={sortLocked} onMinutesChange={(v) => patch(i, { machineTime: v })} {...(!isSorted ? reg(i, 4) : {})} />
                  </td>
                  {isRn && (
                    <>
                      <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
                        <LogBookDateTimeCell value={row.machineStart ?? ""} onChange={(value) => patchActual(i, { machineStart: value })} onCommit={() => onTimingCommit?.("machineStart")} disabled={readOnly || sortLocked} compact baseDate={row.montageStart ?? ""} />
                      </td>
                      <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
                        <LogBookDateTimeCell value={row.machineEnd ?? ""} onChange={(value) => patchActual(i, { machineEnd: value })} onCommit={() => onTimingCommit?.("machineEnd")} disabled={readOnly || sortLocked} compact baseDate={row.montageStart ?? ""} />
                      </td>
                      <LogBookRnDurationCell
                        actual={machineRnTime}
                        planned={row.machineTime}
                        className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-right tabular-nums text-[11px] text-gray-700 bg-gray-50"
                      />
                    </>
                  )}
                  <td className={`border border-gray-300 h-[26px] px-1.5 py-0.5${planCellCls}`}>
                    <HoursInput className={planInputCls} minutes={row.dismountCleanTime} readOnly={planLocked || sortLocked} disabled={sortLocked} onMinutesChange={(v) => patch(i, { dismountCleanTime: v })} {...(!isSorted ? reg(i, 5) : {})} />
                  </td>
                  {isRn && (
                    <>
                      <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
                        <LogBookDateTimeCell value={row.dismountStart ?? ""} onChange={(value) => patchActual(i, { dismountStart: value })} onCommit={() => onTimingCommit?.("dismountStart")} disabled={readOnly || sortLocked} compact baseDate={row.montageStart ?? ""} />
                      </td>
                      <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
                        <LogBookDateTimeCell value={row.dismountEnd ?? ""} onChange={(value) => patchActual(i, { dismountEnd: value })} onCommit={() => onTimingCommit?.("dismountEnd")} disabled={readOnly || sortLocked} compact baseDate={row.montageStart ?? ""} />
                      </td>
                      <LogBookRnDurationCell
                        actual={dismountRnTime}
                        planned={row.dismountCleanTime}
                        className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-right tabular-nums text-[11px] text-gray-700 bg-gray-50"
                      />
                    </>
                  )}
                  <td className={`border border-gray-300 h-[26px] px-1.5 py-0.5${planCellCls}`}>
                    <HoursInput className={planInputCls} minutes={row.cleaningTime} readOnly={planLocked || sortLocked} disabled={sortLocked} onMinutesChange={(v) => patch(i, { cleaningTime: v })} {...(!isSorted ? reg(i, 6) : {})} />
                  </td>
                  {isRn && (
                    <>
                      <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
                        <LogBookDateTimeCell value={row.cleaningStart ?? ""} onChange={(value) => patchActual(i, { cleaningStart: value })} onCommit={() => onTimingCommit?.("cleaningStart")} disabled={readOnly || sortLocked} compact baseDate={row.montageStart ?? ""} />
                      </td>
                      <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
                        <LogBookDateTimeCell value={row.cleaningEnd ?? ""} onChange={(value) => patchActual(i, { cleaningEnd: value })} onCommit={() => onTimingCommit?.("cleaningEnd")} disabled={readOnly || sortLocked} compact baseDate={row.montageStart ?? ""} />
                      </td>
                      <LogBookRnDurationCell
                        actual={cleaningRnTime}
                        planned={row.cleaningTime}
                        className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-right tabular-nums text-[11px] text-gray-700 bg-gray-50"
                      />
                    </>
                  )}
                  <td className={`border border-gray-300 h-[26px] px-1.5 py-0.5${planCellCls}`}>
                    <input type="number" className={planInputCls} value={row.operatorCount} readOnly={planLocked || sortLocked} disabled={sortLocked} onChange={(e) => patch(i, { operatorCount: e.target.value })} {...(!isSorted ? reg(i, 7) : {})} />
                  </td>
                  <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-right tabular-nums text-[11px] text-gray-700 bg-gray-50">
                    {total > 0 ? minutesToHoursText(total) : ""}
                  </td>
                  {isRn && (
                    <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-right tabular-nums text-[11px] text-gray-700 bg-gray-50">
                      {minutesToHoursText(totalRnTime)}
                    </td>
                  )}
                  {isRn && (
                    <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-right tabular-nums text-[11px] text-gray-700 bg-gray-50">
                      {minutesToHoursText(elapsedMinutes(row))}
                    </td>
                  )}
                  <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-center text-[11px] text-gray-700 bg-gray-50">
                    {LOGBOOK_TIME_UNIT}
                  </td>
                </tr>
                </React.Fragment>
              );
            })}
          </tbody>
          {isSummaryMode && !isRn && summaryTotals && (
            <tfoot>
              <tr className="bg-gray-100 font-semibold">
                <td className={`${textCellCls} font-semibold`}>Вкупно</td>
                <td className={`${rightTextCellCls} font-semibold`}>{summaryTotals.montageTime}</td>
                <td className={`${rightTextCellCls} font-semibold`}>{summaryTotals.machineTime}</td>
                <td className={`${rightTextCellCls} font-semibold`}>{summaryTotals.dismountCleanTime}</td>
                <td className={`${rightTextCellCls} font-semibold`}>{summaryTotals.cleaningTime}</td>
                <td className={rightTextCellCls} />
                <td className={`${rightTextCellCls} font-semibold`}>{summaryTotals.total}</td>
                <td className={centerTextCellCls}>{LOGBOOK_TIME_UNIT}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      </FitWidthContext.Provider>
    </div>
  );
};

export default OSProductivityTable;
